import Alert from '@code-dot-org/component-library/alert';
import classNames from 'classnames';
import moment from 'moment';
import React, {forwardRef, memo} from 'react';

import AiTutorVersionActionNotification from '@cdo/apps/aiComponentLibrary/aiTutorVersionActionNotification/AiTutorVersionActionNotification';
import ChatMessage from '@cdo/apps/aiComponentLibrary/chatMessage/ChatMessage';
import {Role} from '@cdo/apps/aiComponentLibrary/chatMessage/types';
import {useAppDispatch} from '@cdo/apps/util/reduxHooks';
import {AiChatTeacherFeedback as TeacherFeedback} from '@cdo/generated-scripts/sharedConstants';

import {modelDescriptions, MODEL_PARAMETER_LABELS} from '../constants';
import {removeUpdateMessage} from '../redux';
import {
  AI_TUTOR_VERSION_ACTION_ACCEPT,
  AI_TUTOR_VERSION_ACTION_REJECT,
  ChatEvent,
  ModelUpdate,
  Notification,
  isChatMessage,
  isNotification,
  isModelUpdate,
  ChatEventDescriptionKey,
  ChatAsset,
  ModelParameters,
  isCompletedChatMessage,
} from '../types';

import ChatMessageView, {getChatMessageDisplayText} from './ChatMessageView';

import styles from './chatWorkspace.module.scss';

const timestampToLocalTime = (timestamp: number) =>
  moment(timestamp).format('LT');

// Alert style by notification kind; kinds left out show as success.
const ALERT_TYPES: {
  [kind in Notification['notificationType']]?: 'danger' | 'warning' | 'info';
} = {
  error: 'danger',
  permissionsError: 'danger',
  warning: 'warning',
  info: 'info',
};

const chatEventDescriptionsOwner = {
  CLEAR_CHAT: 'You cleared the chat workspace.',
  LOAD_LEVEL: 'You loaded the level.',
} as const satisfies {[key in ChatEventDescriptionKey]: string};

const chatEventDescriptionsStudent = {
  CLEAR_CHAT: 'The user cleared the chat workspace.',
  LOAD_LEVEL: 'The user loaded the level.',
} as const satisfies {[key in ChatEventDescriptionKey]: string};

interface ChatEventViewProps extends React.HTMLAttributes<HTMLDivElement> {
  event: ChatEvent;
  isTeacherView?: boolean;
  buildAssetUrl?: (asset: ChatAsset) => string;
  clientType?: string;
  modelParameters?: ModelParameters;
  postText?: React.ReactNode;
}

function formatModelUpdateText(update: ModelUpdate): string {
  const {updatedField, updatedValue, timestamp} = update;
  const fieldLabel = MODEL_PARAMETER_LABELS[updatedField]!;

  let updatedToText = undefined;
  if (updatedField === 'temperature') {
    updatedToText = updatedValue as number;
  }
  if (updatedField === 'selectedModelId') {
    updatedToText = modelDescriptions.find(
      model => model.id === updatedValue
    )?.name;
  }

  const modelUpdateText = updatedToText
    ? `${fieldLabel} has been updated to ${updatedToText.toString()}. ${timestampToLocalTime(
        timestamp
      )}`
    : `${fieldLabel} has been updated. ${timestampToLocalTime(timestamp)}`;

  return modelUpdateText;
}

/**
 * Renders AI Chat {@link ChatEvent}s using common AI design components.
 */
const ChatEventView = forwardRef<HTMLDivElement, ChatEventViewProps>(
  (
    {
      event,
      isTeacherView,
      buildAssetUrl,
      tabIndex,
      onKeyDown,
      clientType,
      modelParameters,
      postText,
    },
    ref
  ) => {
    const dispatch = useAppDispatch();

    const chatEventDescriptions = isTeacherView
      ? chatEventDescriptionsStudent
      : chatEventDescriptionsOwner;

    // Only wrap chat messages in a focusable div for keyboard navigation
    if (isChatMessage(event)) {
      const teacherFlagged =
        isCompletedChatMessage(event) &&
        event.teacherFeedback === TeacherFeedback.CLEAN_DISAGREE;
      return (
        <div
          ref={ref}
          tabIndex={tabIndex}
          onKeyDown={onKeyDown}
          aria-label={getChatMessageDisplayText(
            event.status,
            event.role,
            event.chatMessageText,
            false, // Profane messages are never shown in the aria-label context to prevent screen readers from reading inappropriate content.
            teacherFlagged
          )}
          className={styles.chatMessageOutline}
        >
          <ChatMessageView
            chatMessage={event}
            isTeacherView={isTeacherView || false}
            buildAssetUrl={buildAssetUrl}
            clientType={clientType}
            modelParameters={modelParameters}
            postText={postText}
            teacherFlagged={teacherFlagged}
          />
        </div>
      );
    }

    if (isNotification(event)) {
      const {
        removeId,
        text,
        notificationType,
        files,
        timestamp,
        commitDescription,
        hideTimestamp,
      } = event;

      if (
        notificationType === 'welcomeMessage' ||
        notificationType === 'hostMessage'
      ) {
        return (
          <div
            ref={ref}
            tabIndex={tabIndex}
            onKeyDown={onKeyDown}
            aria-label={`Assistant: ${text}`}
            className={styles.chatMessageOutline}
          >
            <ChatMessage role={Role.ASSISTANT} text={text} />
          </div>
        );
      }

      // Use special notification component for AI tutor version actions.
      if (
        notificationType === AI_TUTOR_VERSION_ACTION_ACCEPT ||
        notificationType === AI_TUTOR_VERSION_ACTION_REJECT
      ) {
        return (
          <AiTutorVersionActionNotification
            text={text}
            type={
              notificationType === AI_TUTOR_VERSION_ACTION_ACCEPT
                ? 'accept'
                : 'reject'
            }
            ref={ref}
            tabIndex={tabIndex}
            onKeyDown={onKeyDown}
            aria-label={`Notification: ${text}`}
            className={styles.chatMessageOutline}
            files={files}
            commitDescription={commitDescription}
          />
        );
      }

      const time = hideTimestamp ? '' : timestampToLocalTime(timestamp);
      return (
        <Alert
          text={time ? `${text} ${time}` : text}
          type={ALERT_TYPES[notificationType] ?? 'success'}
          onClose={
            isTeacherView
              ? undefined
              : () => dispatch(removeUpdateMessage(removeId))
          }
          size="s"
          ref={ref}
          tabIndex={tabIndex}
          onKeyDown={onKeyDown}
          aria-label={
            time
              ? `Notification: ${text}, Time: ${time}`
              : `Notification: ${text}`
          }
          className={styles.chatMessageOutline}
        />
      );
    }

    if (isModelUpdate(event)) {
      return (
        <Alert
          className={classNames(
            'uitest-aichat-chat-alert',
            styles.chatMessageOutline
          )}
          text={formatModelUpdateText(event)}
          type="success"
          size="s"
          onClose={
            isTeacherView
              ? undefined
              : () => dispatch(removeUpdateMessage(event.removeId))
          }
          ref={ref}
          tabIndex={tabIndex}
          onKeyDown={onKeyDown}
          aria-label={formatModelUpdateText(event)}
        />
      );
    }

    // Automatically narrowed to UserActionEvent
    return (
      <Alert
        text={chatEventDescriptions[event.descriptionKey]}
        type="info"
        size="s"
        ref={ref}
        tabIndex={tabIndex}
        onKeyDown={onKeyDown}
        aria-label={chatEventDescriptions[event.descriptionKey]}
        className={styles.chatMessageOutline}
      />
    );
  }
);

export default memo(ChatEventView);
